<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useApiKeys } from './useApiKeys';
import { useToast } from '@/composables/useToast';
import ApiKeyModal from './ApiKeyModal.vue';

const { addToast } = useToast();
const { apiKeys, loading, loadApiKeys, createApiKey, revokeApiKey } = useApiKeys();

const showModal = ref(false);
const newlyGeneratedKey = ref<string | null>(null);

onMounted(() => {
    loadApiKeys();
});

const handleGenerate = async (name: string, scopes: string[]) => {
    try {
        const rawKey = await createApiKey(name, scopes);
        newlyGeneratedKey.value = rawKey;
        showModal.value = false;
        addToast('Clé API générée avec succès.', 'success');
    } catch (e: any) {
        addToast(e.message, 'error');
    }
};

const handleRevoke = async (id: number) => {
    if (!confirm('Voulez-vous vraiment révoquer cette clé API ? Cette action est irréversible.')) return;
    
    try {
        await revokeApiKey(id);
        addToast('Clé API révoquée avec succès.', 'success');
    } catch (e: any) {
        addToast('Erreur lors de la révocation.', 'error');
    }
};

const copyToClipboard = async () => {
    if (newlyGeneratedKey.value) {
        try {
            await navigator.clipboard.writeText(newlyGeneratedKey.value);
            addToast('Clé copiée dans le presse-papier !', 'success');
        } catch (e) {
            addToast('Erreur lors de la copie.', 'error');
        }
    }
};

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};
</script>

<template>
    <div class="card border-0 shadow-sm">
        <div class="card-body p-4">
            <div class="d-flex justify-content-between align-items-center mb-4">
                <h4 class="mb-0 fw-bold">Clés API (Personal Access Tokens)</h4>
                <button class="btn btn-primary d-flex align-items-center gap-2" @click="showModal = true">
                    <i class="fas fa-plus"></i>
                    Créer une clé
                </button>
            </div>

            <div v-if="newlyGeneratedKey" class="alert alert-success border-0 mb-4 shadow-sm">
                <h5 class="alert-heading fw-bold mb-3">
                    <i class="fas fa-check-circle me-2"></i>Clé générée avec succès !
                </h5>
                <p class="mb-2">Copiez cette clé immédiatement. <strong>Elle ne sera plus jamais affichée.</strong></p>
                <div class="input-group">
                    <input type="text" class="form-control font-monospace bg-white" readonly :value="newlyGeneratedKey" />
                    <button class="btn btn-success" @click="copyToClipboard">
                        <i class="fas fa-copy"></i> Copier
                    </button>
                </div>
                <button class="btn btn-sm btn-outline-success mt-3" @click="newlyGeneratedKey = null">J'ai bien copié la clé</button>
            </div>

            <div v-if="loading && apiKeys.length === 0" class="text-center text-muted py-5">
                <div class="spinner-border text-secondary mb-3" role="status"></div>
                <div>Chargement des clés API...</div>
            </div>

            <div v-else-if="apiKeys.length === 0" class="text-center text-muted py-5 border rounded bg-light">
                <i class="fas fa-key fa-3x mb-3 text-secondary opacity-50"></i>
                <h5>Aucune clé API</h5>
                <p class="mb-0">Vous n'avez pas encore généré de clé d'accès.</p>
            </div>

            <div v-else class="table-responsive">
                <table class="table table-hover align-middle">
                    <thead class="table-light">
                        <tr>
                            <th>Nom</th>
                            <th>Droits (Scopes)</th>
                            <th>Dernière utilisation</th>
                            <th>Créée le</th>
                            <th class="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="apiKey in apiKeys" :key="apiKey.id">
                            <td class="fw-medium">{{ apiKey.name }}</td>
                            <td>
                                <div class="d-flex flex-wrap gap-1">
                                    <span v-for="scope in apiKey.scopes" :key="scope" class="badge bg-secondary">
                                        {{ scope.replace('sport:', '') }}
                                    </span>
                                </div>
                            </td>
                            <td>
                                <span v-if="apiKey.lastUsedAt">{{ formatDate(apiKey.lastUsedAt) }}</span>
                                <span v-else class="text-muted fst-italic">Jamais</span>
                            </td>
                            <td class="text-muted">{{ formatDate(apiKey.createdAt) }}</td>
                            <td class="text-end">
                                <button class="btn btn-outline-danger btn-sm" title="Révoquer la clé" @click="handleRevoke(apiKey.id)">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <ApiKeyModal 
            :show="showModal" 
            @close="showModal = false" 
            @generate="handleGenerate"
        />
    </div>
</template>
